import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import Reveal from '@/components/feature/Reveal';

interface Branch {
  id: number;
  tag: string;
  name: string;
  address: string;
  embed: string;
  mapLink: string;
  hours: string;
}

export default function Locations() {
  const { t } = useTranslation();

  const branches: Branch[] = [
    {
      id: 1,
      tag: t('locations.branch.1.tag'),
      name: t('locations.branch.1.name'),
      address: t('locations.branch.1.address'),
      embed: 'https://www.google.com/maps?q=24.3208148,54.6145732&z=17&output=embed',
      mapLink: 'https://www.google.com/maps/place/Biola+cafe/@24.3208148,54.6145732,17z/data=!3m1!4b1!4m6!3m5!1s0x3e5e37fb9bc21c8b:0x1d7ba12956d2b8b0!8m2!3d24.3208148!4d54.6145732!16s%2Fg%2F11y0h63wr3?entry=ttu',
      hours: t('locations.branch.1.hours'),
    },
    {
      id: 2,
      tag: t('locations.branch.2.tag'),
      name: t('locations.branch.2.name'),
      address: t('locations.branch.2.address'),
      embed: 'https://www.google.com/maps?q=24.2912998,54.6538811&z=17&output=embed',
      mapLink: 'https://www.google.com/maps/place/24%C2%B017\'28.7%22N+54%C2%B039\'23.2%22E/@24.2912998,54.6538811,17z/data=!3m1!4b1!4m4!3m3!8m2!3d24.2912998!4d54.656456?hl=en&entry=ttu',
      hours: t('locations.branch.2.hours'),
    },
    {
      id: 3,
      tag: t('locations.branch.3.tag'),
      name: t('locations.branch.3.name'),
      address: t('locations.branch.3.address'),
      embed: 'https://www.google.com/maps?q=Auto+Cafe+ADNOC+523+Aldaar+Khalifa+City+SE45+Abu+Dhabi&z=17&output=embed',
      mapLink: 'https://www.google.com/maps/dir//Auto+Cafe,+adnoc+523+Aldaar+-+Khalifa+City+-+SE45+-+Abu+Dhabi+-+United+Arab+Emirates/@24.3868306,54.5586204,11z/data=!4m8!4m7!1m0!1m5!1m1!1s0x3e5e45cb14a278ff:0x4c2ec175dc0de281!2m2!1d54.6031894!2d24.44101?entry=ttu',
      hours: t('locations.branch.3.hours'),
    },
  ];

  return (
    <Layout showTopBar>
      {/* Hero */}
      <section className="relative w-full h-[360px] md:h-[440px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20cafe%20exterior%20storefront%20at%20dusk%20with%20warm%20glowing%20lights%2C%20modern%20minimalist%20architecture%2C%20palm%20trees%2C%20inviting%20entrance%2C%20editorial%20photography%2C%20soft%20golden%20hour%20light%2C%20premium%20coffee%20shop%20ambiance&width=1600&height=700&seq=locations-hero-01&orientation=landscape"
          alt="BIOLA Cafe Locations"
          className="w-full h-full object-cover object-center animate-hero-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/45 via-foreground-950/35 to-foreground-950/55" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center max-w-2xl">
            <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-xs font-semibold rounded-full mb-4 animate-hero-content">
              {t('locations.pageBadge')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3 leading-tight animate-hero-content-delay">
              {t('locations.pageTitle')}
            </h1>
            <p className="text-background-100 text-base md:text-lg max-w-xl mx-auto leading-relaxed animate-hero-content-delay-2">
              {t('locations.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-10">
          {branches.map((branch, i) => (
            <Reveal key={branch.id} delay={i * 60}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-8 bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                {/* Map */}
                <div className="w-full h-[300px] md:h-[380px] lg:h-full lg:min-h-[380px]">
                  <iframe
                    src={branch.embed}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={branch.name}
                    className="w-full h-full"
                  />
                </div>

                {/* Info */}
                <div className="p-5 md:p-8 flex flex-col justify-center">
                  <span className="inline-flex items-center gap-2 w-fit px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-4">
                    <i className="ri-store-2-line" />
                    {branch.tag}
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                    {branch.name}
                  </h2>
                  <div className="flex items-start gap-3 mb-4">
                    <span className="w-9 h-9 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-2-line" />
                    </span>
                    <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                      {branch.address}
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mb-6">
                    <span className="w-9 h-9 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-time-line" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900 mb-0.5">
                        {t('locations.hoursLabel')}
                      </p>
                      <p className="text-foreground-500 text-sm">{branch.hours}</p>
                    </div>
                  </div>
                  <a
                    href={branch.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap w-full sm:w-fit"
                  >
                    <i className="ri-direction-line transition-transform duration-300 group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                    {t('locations.getDirections')}
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How to Reach CTA */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-primary-500">
        <Reveal>
          <div className="max-w-2xl mx-auto text-center">
            <span className="w-12 h-12 flex items-center justify-center bg-background-50/20 backdrop-blur-sm text-background-50 rounded-xl mx-auto mb-4">
              <i className="ri-navigation-line text-2xl" />
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              {t('locations.ctaTitle')}
            </h2>
            <p className="text-background-100 text-sm md:text-base leading-relaxed mb-6">
              {t('locations.ctaDesc')}
            </p>
            <a
              href={branches[0].mapLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-press group inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-primary-600 font-semibold rounded-xl hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-map-2-line" />
              {t('locations.ctaButton')}
            </a>
          </div>
        </Reveal>
      </section>
    </Layout>
  );
}